import type { AppListenerApi } from "@src/store/rootState";
import { experienceBuilderAiSlice } from "@src/store/slices/experienceBuilder/ai/slice";
import { getHiddenVariationMods } from "@src/store/slices/experienceBuilder/common/selectors/getHiddenVariationMods";
import { reconcileDraftWithMods } from "@src/store/slices/experienceBuilder/common/thunks/reconcileDraftWithMods";
import { getModErrors } from "@src/store/slices/experienceBuilder/common/utils/getModErrors";
import { aiModsToFullFormat } from "@src/store/slices/experienceBuilder/common/utils/modHandlerRegistry";
import { normalizeMod } from "@src/store/slices/experienceBuilder/common/utils/normalizeMod";
import { experienceBuilderSlice } from "@src/store/slices/experienceBuilder/slice";
import { getCanCreateBundleOnsiteUpsell } from "@src/store/slices/organization/selectors/getCanCreateBundleOnsiteUpsell";

import { MAX_AUTO_VERIFY_ATTEMPTS } from "../constants";
import { matchUiAction } from "../detect";
import { Continuation, Matcher, UiActionResult } from "../types";
import { applyAndSettle } from "./settle";

const { _setAutoVerifyAttempt } = experienceBuilderAiSlice.actions;
const { getAutoVerifyAttempt } = experienceBuilderAiSlice.selectors;

// Apply an `update_mods` ui-action — an upsert-by-id plus an explicit
// delete-by-id, leaving every other mod untouched. Relocated from the
// experience-builder slice's extraReducer matcher so every turn-ending tool's
// effect lives with its handler. `upsert` entries resolve against the
// CURRENTLY PREVIEWED variation (existing-mod lookup in `aiModsToFullFormat`
// is scoped to it); the AI calls `setVariation` first to change it.
//
// Nothing is variation-scoped by the time it reaches the slice's `updateMods`
// reducer — it upserts and removes by id alone. The scoping is enforced
// upstream: `update_mods` rejects an id belonging to another variation before
// the ui-action is emitted at all.
//
// An `upsert` entry REPLACES the mod it names: every AI-visible field is
// required or schema-defaulted, so `aiModsToFullFormat`'s spread of the
// payload over the existing mod overwrites rather than patches.
//
// Upserting a mod the merchant has HIDDEN shows it again as part of the same
// apply (ADR 0011, superseded point 4). Hiding is filtered in `getEmbeddedState`
// against `hiddenModIds`, a set held outside `mods`, so writing `mods` alone
// would land the edit under a mod that stays filtered out — the merchant's
// comparison silently now says something different. Showing it is the only way
// the edit they asked for is one they can see. The AI is not consulted and is
// not even told which mods are hidden; this is the frontend's own resolution.
// `delete` is deliberately NOT covered: hidden ids are never pruned (
// `getHiddenVariationMods` intersects with the variation's mods, so a stale id
// is harmless) and showing a mod on its way out means nothing.
//
// A mod that fails `getModErrors` never reaches the store. The schema only
// checks that fields are PRESENT, so a mod can parse and still be blank (an
// injection with neither `js` nor `css`, a text mod with an empty `replace`
// and `remove: false`). Left to reach Redux, the two consumers disagree:
// `getEmbeddedState` skips it, so the merchant never sees it, while
// `getConvertedMods` has no such filter and saves it anyway. Rejecting here
// keeps them consistent by construction, and the rejects come back so the
// continuation can tell the AI instead of reporting "Done!".
//
// Rejection is per mod, not per call: `upsert` entries are independent, so a
// blank one shouldn't discard the valid work alongside it.
export type RejectedMod = { id: string; description?: string; reason: string };

// Exported for unit tests.
export const applyUpdateMods = (
  results: UiActionResult,
  api: AppListenerApi
): RejectedMod[] => {
  const upserted = Array.isArray(results.upserted) ? results.upserted : [];
  const deleted = Array.isArray(results.deleted) ? results.deleted : [];
  if (upserted.length === 0 && deleted.length === 0) {
    return [];
  }

  const state = api.getState();
  const variationId = experienceBuilderSlice.selectors.getVariationId(state);
  if (!variationId) {
    return [];
  } // no active variation — nothing to scope to
  const mods = experienceBuilderSlice.selectors.getMods(state);
  const location = experienceBuilderSlice.selectors.getLocation(state);

  const incoming = aiModsToFullFormat(
    upserted,
    mods,
    location ?? undefined,
    variationId
  );

  const valid: typeof incoming = [];
  const rejected: RejectedMod[] = [];
  for (const mod of incoming) {
    if (
      mod.type === "bundleOnsiteUpsell" &&
      !getCanCreateBundleOnsiteUpsell(state)
    ) {
      rejected.push({
        id: mod.id,
        description: mod.description,
        reason:
          "bundles are not included in this store's plan. Do not re-send it; tell the merchant bundles need a plan with Smart Merchandising",
      });
      continue;
    }
    // Normalized first so the check matches what the preview would apply —
    // a whitespace-only `replace` trims to empty and is just as blank.
    const errors = Object.values(getModErrors(normalizeMod(mod)));
    if (errors.length === 0) {
      valid.push(mod);
      continue;
    }
    rejected.push({
      id: mod.id,
      description: mod.description,
      reason: [...new Set(errors)].join("; "),
    });
  }

  if (valid.length > 0 || deleted.length > 0) {
    api.dispatch(
      experienceBuilderSlice.actions.updateMods({
        set: valid.length ? valid : undefined,
        remove: deleted.length ? deleted : undefined,
      })
    );
    // Keep an open config-panel draft in step with what Viv just committed.
    reconcileDraftWithMods(api, { set: valid, remove: deleted });

    // After the write, so a mod this call CREATED can't collide with a stale
    // hidden id, and absolute (`hidden: false`) rather than a toggle — this
    // means "show these", never "flip these". Same synchronous effect as the
    // write above, so one `applyAndSettle` covers both and `previewChanged`
    // reports the combined result.
    const hiddenIds = new Set(
      getHiddenVariationMods(api.getState()).map((mod) => mod.id)
    );
    const toShow = valid.map((mod) => mod.id).filter((id) => hiddenIds.has(id));
    if (toShow.length > 0) {
      api.dispatch(
        experienceBuilderSlice.actions._setModsHidden({
          modificationIds: toShow,
          hidden: false,
        })
      );
    }
  }
  return rejected;
};

/**
 * The follow-up once update_mods has settled: a correction turn if anything was
 * rejected, else auto-verify (capped per turn), else an assistant summary
 * covering both the upserted and the deleted mods. Mutates the auto-verify
 * counter as a side effect (increment on opt-in verify, reset otherwise).
 * Exported for unit tests.
 */
export function updateModsContinuation(
  results: UiActionResult,
  api: AppListenerApi,
  rejected: RejectedMod[] = [],
  previewChanged = true
): Continuation {
  // A rejected mod outranks verifying: there is nothing to look at for it, and
  // the AI is the only one who can fix it. It can't be told in-turn — the tool
  // already returned — so this fires a fresh turn. Valid mods in the same call
  // were applied, so the message has to say so or the AI will re-send them.
  if (rejected.length > 0) {
    api.dispatch(_setAutoVerifyAttempt(0));
    const detail = rejected
      .map(
        (mod) =>
          `${mod.description ? `"${mod.description}" ` : ""}(id ${mod.id}): ${mod.reason}`
      )
      .join("\n");
    const applied =
      Array.isArray(results.upserted) &&
      results.upserted.length > rejected.length
        ? " Your other changes in this call WERE applied — do not re-send them."
        : "";
    return {
      kind: "user",
      message:
        `[system] ${rejected.length === 1 ? "1 mod was" : `${rejected.length} mods were`} NOT applied:\n${detail}\n` +
        `Fix and re-send via update_mods unless the reason says otherwise.${applied}`,
    };
  }

  // Verification is visual: it fires a whole extra turn that screenshots the
  // preview. When the applied change left the preview untouched there is
  // nothing in it to look at, so the round can only conclude "not applied" —
  // burning a turn (up to MAX_AUTO_VERIFY_ATTEMPTS of them) to confirm
  // something the preview structurally cannot show.
  //
  // The live case is deleting a mod the merchant has hidden: it was already
  // filtered out of the preview, so removing it changes nothing on screen.
  // Editing a hidden mod is NOT a case here — `applyUpdateMods` shows the mod
  // as it applies, which puts its effect on screen and moves the preview.
  // `previewChanged` is `applyAndSettle`'s own answer, so this shares one
  // definition of "visibly changed" with the settle wait rather than
  // inventing a second.
  if (results.verify === true && !previewChanged) {
    api.dispatch(_setAutoVerifyAttempt(0));
    return { kind: "assistant", message: "Done!" };
  }

  // Auto-verify only when the LLM opted in via `verify: true` AND we haven't
  // already exhausted our per-turn retries. Otherwise fall through to an
  // assistant message.
  const attempt = getAutoVerifyAttempt(api.getState());
  if (results.verify === true && attempt < MAX_AUTO_VERIFY_ATTEMPTS) {
    api.dispatch(_setAutoVerifyAttempt(attempt + 1));
    return { kind: "auto-verify" };
  }
  api.dispatch(_setAutoVerifyAttempt(0));
  // If the LLM wanted to verify but we hit the cap, it didn't converge on its
  // own. Defer to the user rather than claiming "Done!" — they can tell us
  // whether the last attempt worked.
  if (results.verify === true) {
    return { kind: "assistant", message: "How does it look now?" };
  }
  const upserted = Array.isArray(results.upserted) ? results.upserted : [];
  const deleted = Array.isArray(results.deleted) ? results.deleted : [];
  const descriptions = upserted
    .map((m) => (m as { description?: string }).description)
    .filter(Boolean) as string[];
  if (deleted.length > 0) {
    descriptions.push(
      deleted.length === 1 ? "Removed 1 mod" : `Removed ${deleted.length} mods`
    );
  }
  const message =
    descriptions.length === 0
      ? "Done!"
      : descriptions.length === 1
        ? `Done! ${descriptions[0]}.`
        : `Done! Applied ${descriptions.length} changes:\n${descriptions.map((d) => `• ${d}`).join("\n")}`;
  return { kind: "assistant", message };
}

export const updateMods: Matcher = {
  match: (part) => {
    const results = matchUiAction(part, "updateMods");
    if (!results) {
      return null;
    }
    return async (api) => {
      let rejected: RejectedMod[] = [];
      const previewChanged = await applyAndSettle(api, () => {
        rejected = applyUpdateMods(results, api);
      });
      return updateModsContinuation(results, api, rejected, previewChanged);
    };
  },
};
